/**
 * Progressive enhancement for the detail components.
 *
 * The components render as static markup in both apps — the public site ships
 * no framework JavaScript on detail pages, so the photo carousel and the
 * sticky contact bar cannot be React state. Both apps call this instead: the
 * web pages from an inline script, the admin preview modal from an effect.
 *
 * Returns a cleanup function that removes every listener and observer.
 */
export function attachDetailBehavior(root: ParentNode): () => void {
  const cleanups = [attachCarousel(root), attachStickyCta(root), revealPastBadge(root)];
  return () => cleanups.forEach((fn) => fn());
}

function attachCarousel(root: ParentNode): () => void {
  const wrap = root.querySelector<HTMLElement>('[data-vp-carousel]');
  const track = root.querySelector<HTMLElement>('[data-vp-track]');
  if (!wrap || !track) return noop;

  const thumbs = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-vp-thumb]'));
  const prevBtn = root.querySelector<HTMLButtonElement>('[data-vp-prev]');
  const nextBtn = root.querySelector<HTMLButtonElement>('[data-vp-next]');
  const total = Number(wrap.dataset.photoCount ?? 0);
  if (total <= 0) return noop;

  let current = 0;
  const goTo = (n: number) => {
    current = ((n % total) + total) % total;
    track.style.transform = `translateX(-${current * 100}%)`;
    thumbs.forEach((thumb, i) => {
      const active = i === current;
      thumb.classList.toggle('vp-thumb-active', active);
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  };

  const onThumb = (thumb: HTMLButtonElement) => () => goTo(Number(thumb.dataset.index ?? 0));
  const thumbHandlers = thumbs.map((thumb) => {
    const handler = onThumb(thumb);
    thumb.addEventListener('click', handler);
    return [thumb, handler] as const;
  });

  const onPrev = () => goTo(current - 1);
  const onNext = () => goTo(current + 1);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goTo(current - 1);
    if (e.key === 'ArrowRight') goTo(current + 1);
  };
  prevBtn?.addEventListener('click', onPrev);
  nextBtn?.addEventListener('click', onNext);
  wrap.addEventListener('keydown', onKey);

  return () => {
    thumbHandlers.forEach(([thumb, handler]) => thumb.removeEventListener('click', handler));
    prevBtn?.removeEventListener('click', onPrev);
    nextBtn?.removeEventListener('click', onNext);
    wrap.removeEventListener('keydown', onKey);
  };
}

function attachStickyCta(root: ParentNode): () => void {
  const stickyCta = root.querySelector<HTMLElement>('[data-vp-sticky-cta]');
  const ctaRow = root.querySelector<HTMLElement>('[data-vp-cta-row]');
  if (!stickyCta || !ctaRow || typeof IntersectionObserver === 'undefined') return noop;

  /* The public site anchors the hide on its page footer; the preview modal has
     none, in which case the bar simply stays up once the inline row scrolls by. */
  const pageFooter = root.querySelector<HTMLElement>('footer');

  let ctaPassed = false;
  let footerVisible = false;
  const update = () => {
    const show = ctaPassed && !footerVisible;
    stickyCta.classList.toggle('vp-sticky-show', show);
    stickyCta.setAttribute('aria-hidden', show ? 'false' : 'true');
  };

  const ctaObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry) return;
      ctaPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    },
    { threshold: 0 },
  );
  ctaObserver.observe(ctaRow);

  let footerObserver: IntersectionObserver | undefined;
  if (pageFooter) {
    footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        footerVisible = entry.isIntersecting;
        update();
      },
      { threshold: 0 },
    );
    footerObserver.observe(pageFooter);
  }

  return () => {
    ctaObserver.disconnect();
    footerObserver?.disconnect();
  };
}

/**
 * Event pages are prerendered and outlive the events they describe, so the
 * "past event" badge starts hidden and is revealed here against the clock of
 * whoever is looking.
 */
function revealPastBadge(root: ParentNode): () => void {
  const badge = root.querySelector<HTMLElement>('[data-vp-past-badge]');
  const endsAt = badge?.dataset.endsAt;
  if (badge && endsAt && new Date(endsAt).getTime() < Date.now()) {
    badge.classList.remove('hidden');
  }
  return noop;
}

function noop(): void {}
